import { useQuery } from '@tanstack/react-query'
import { Landmark } from 'lucide-react'
import { taxApi } from '../api/client'
import type { TaxLotRow } from '../api/client'
import { relativeTime } from '../lib/relativeTime'
import { useExpandablePanel } from '../hooks/useExpandablePanel'
import { ExpandToggle } from './ExpandToggle'
import { InfoTip } from './InfoTip'
import { reconcile } from '../lib/taxReconcile'
import './TaxSummaryCard.css'

// Same "not materialized yet" copy shown by the outer card (below) and the
// expanded lots section — a single string so the two can't drift apart.
const NOT_AVAILABLE_COPY =
  'No tax summary yet — it materializes daily after the first ledger reconciliation.'

interface TaxSummaryCardProps {
  /** Tax year; defaults to the current calendar year. The future tax tab
   *  passes explicit years; the dashboard mounts it bare. */
  year?: number
  /** Account/book to scope to; omitted = default (research/active-pointer)
   *  account, matching prior behavior. Passed by the per-book detail page
   *  so its query key (and cache entry) doesn't collide with the global
   *  instance mounted on /ops. */
  accountId?: number
  /** Pick-source account id when this book is a subscriber/mirror book.
   *  WS-B gives every trading-enabled book its own TaxLedgerService, so a
   *  subscriber book materializes its own tax lots and annual summary once
   *  its ledger reconciles — this card doesn't special-case mirrors.
   *  Accepted for signature parity with ScorecardTable/PairedExcessChart
   *  (BookPage passes the same props to all three); unused here. */
  mirrorsSourceId?: number | null
}

function usd(n: number): string {
  const sign = n < 0 ? '-' : ''
  return `${sign}$${Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

function fmtShares(shares: string): string {
  return Number(shares).toLocaleString(undefined, { maximumFractionDigits: 4 })
}

// Title tooltip for a wash-sale row's badge, built from its own fields —
// no re-derivation, just a reshape of wash_disallowed/wash_recovered.
function washTitle(row: TaxLotRow): string {
  const disallowed = Number(row.wash_disallowed)
  const recovered = Number(row.wash_recovered)
  const parts: string[] = []
  if (disallowed > 0) parts.push(`−${usd(disallowed)} deferred into replacement basis`)
  if (recovered > 0) parts.push(`+${usd(recovered)} basis recovered`)
  return parts.length > 0 ? parts.join(' · ') : 'wash sale'
}

function LotRow({ row }: { row: TaxLotRow }) {
  const gainLoss = Number(row.gain_loss)
  const reportableGainLoss = Number(row.reportable_gain_loss)
  return (
    <tr className="tax-lots-row">
      <td className="mono tax-lots-symbol tax-lots-cell-symbol">{row.symbol}</td>
      <td className="num tax-lots-cell-shares">{fmtShares(row.shares)}</td>
      <td className="tax-lots-dates tax-lots-cell-dates">{row.acquired} → {row.disposed}</td>
      <td className="num mono tax-lots-cell-proceeds" data-label="Proceeds">{usd(Number(row.proceeds))}</td>
      <td className="num mono tax-lots-cell-basis" data-label="Basis">{usd(Number(row.cost_basis))}</td>
      <td className={`num mono ${gainLoss < 0 ? 'delta-down' : 'delta-up'} tax-lots-cell-gl`}>
        {usd(gainLoss)}
      </td>
      <td className={`num mono ${reportableGainLoss < 0 ? 'delta-down' : 'delta-up'} tax-lots-cell-reportable`} data-label="Reportable">
        {usd(reportableGainLoss)}
      </td>
      <td className="tax-lots-cell-term" data-label="Term">
        <span className={`tax-lots-term-chip tax-lots-term-chip--${row.is_long_term ? 'lt' : 'st'}`}>
          {row.is_long_term ? 'LT' : 'ST'}
        </span>
      </td>
      <td className="tax-lots-cell-wash">
        {row.is_wash_sale || Number(row.wash_recovered) > 0 ? (
          <span
            className="tax-lots-wash-badge"
            title={washTitle(row)}
            tabIndex={0}
            aria-label={washTitle(row)}
          >
            WASH
          </span>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
    </tr>
  )
}

export function TaxSummaryCard({ year, accountId }: TaxSummaryCardProps) {
  const taxYear = year ?? new Date().getFullYear()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['tax-summary', taxYear, accountId],
    queryFn: () => taxApi.getSummary(taxYear, accountId),
    refetchInterval: 60_000,
    retry: 1,
  })

  // Ledger drill-down (Task 6): the panel-expand idiom shared by 8 feed/list
  // cards, keyed per book so the persisted expand state doesn't collide
  // across the global /ops instance and per-book detail pages.
  // `scrollClassName` is deliberately unused here — this panel's horizontal
  // scroll is owned by `.tax-lots-scroll` below, not the hook's default.
  const { expanded, toggle } = useExpandablePanel(`tax-lots:${accountId ?? 'default'}`)
  const {
    data: lotsData,
    isLoading: lotsLoading,
    isError: lotsIsError,
  } = useQuery({
    queryKey: ['tax-lots', taxYear, accountId],
    queryFn: () => taxApi.getLots(taxYear, accountId),
    enabled: expanded,
    refetchInterval: 60_000,
    retry: 1,
  })

  const reportable = data?.total_net_gain_loss ?? 0
  const washDeferred = data?.wash_sale_disallowed ?? 0
  const washRecovered = data?.wash_sale_recovered ?? 0
  // Server-computed Σ(proceeds − unadjusted basis); the old subtraction is
  // only a fallback for a pre-042 API and double-counts recovered deferrals.
  const economic = data?.economic_net_gain_loss ?? (reportable - washDeferred)
  // Deferral still parked in basis = disallowed − recovered (year-scoped).
  const deferredNet = washDeferred - washRecovered
  const washCount = data?.transactions?.wash_sales ?? 0
  const income = data?.income
  const showLongTerm = (data?.transactions?.long_term ?? 0) > 0

  const lots = lotsData?.lots ?? []
  // Σ(displayed lot columns) — an informational totals row, NOT the
  // reconciliation check itself (that's `reconcileResult` below, which
  // compares the endpoint's `summary` block — computed over the FULL year —
  // against the card's headline figures). When `truncated` is true this
  // totals row covers only the latest 2000 rows, so it will legitimately
  // differ from `summary`/the headline without that being a tripwire
  // condition; we deliberately don't compare Σ(rows) to summary for that
  // reason (spec correction #4).
  const lotsTotals = lots.reduce(
    (acc, r) => ({
      proceeds: acc.proceeds + Number(r.proceeds),
      costBasis: acc.costBasis + Number(r.cost_basis),
      gainLoss: acc.gainLoss + Number(r.gain_loss),
      reportableGainLoss: acc.reportableGainLoss + Number(r.reportable_gain_loss),
    }),
    { proceeds: 0, costBasis: 0, gainLoss: 0, reportableGainLoss: 0 }
  )
  // Pass the RAW economic field (null when the pre-042 fallback is in play),
  // not the fallback-computed `economic` display value above — reconciling
  // against the card's own documented-as-double-counting fallback would
  // alarm on every pre-042 row (minor 12). `reconcile` skips that leg when
  // this is null.
  const economicForReconcile = data?.economic_net_gain_loss ?? null
  const reconcileResult = lotsData?.summary
    ? reconcile(lotsData.summary, { reportable, economic: economicForReconcile, deferredNet })
    : null

  // Staleness check (CRITICAL 1): the headline (`data`) is a materialized
  // AnnualTaxSummary row refreshed once per ET day; `lotsData.summary` is
  // computed live at request time. Any realized sale disposed on/after the
  // calendar day the headline was last calculated will legitimately diverge
  // the two until tonight's recompute — that is not a data-integrity bug and
  // must not trip the red tripwire. `calculated_at` is an ISO datetime whose
  // first 10 chars are the calendar date; `disposed` is already a plain
  // YYYY-MM-DD string in the same (naive-UTC) convention the backend uses
  // for both fields, so a lexical compare is correct without any Date/tz
  // parsing.
  const calculatedAtDate = data?.calculated_at ? data.calculated_at.slice(0, 10) : null
  const staleLots = calculatedAtDate
    ? lots.filter((row) => row.disposed >= calculatedAtDate)
    : []

  return (
    <div className="card tax-summary-card">
      <div className="card-header">
        <h3 className="card-title">
          <Landmark size={20} className="icon" />
          Tax Summary · {taxYear}
        </h3>
        {data?.available && (
          <a
            className="tax-8949-download"
            href={`/api/tax/8949?year=${taxYear}${accountId != null ? `&account_id=${accountId}` : ''}`}
            download
          >
            Download Form 8949
          </a>
        )}
      </div>

      {isLoading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : isError ? (
        <div className="empty-state">
          <div className="empty-text">Tax data unavailable</div>
        </div>
      ) : !data?.available ? (
        <div className="empty-state">
          <div className="empty-text">{NOT_AVAILABLE_COPY}</div>
        </div>
      ) : (
        <>
          <div className="tax-headline">
            <div className="tax-figure">
              <span className="tax-label">
                Realized (economic)
                <InfoTip metric="tax_economic" />
              </span>
              <span className={`tax-value mono ${economic < 0 ? 'neg' : 'pos'}`}>
                {usd(economic)}
              </span>
            </div>
            <div className="tax-figure">
              <span className="tax-label">
                Reportable (tax)
                <InfoTip metric="tax_reportable" />
              </span>
              <span className={`tax-value mono ${reportable < 0 ? 'neg' : 'pos'}`}>
                {usd(reportable)}
              </span>
            </div>
            <div className="tax-figure tax-wash">
              <span className="tax-label">
                Deferred into basis (net) · {washCount} wash sales
                <InfoTip metric="tax_deferred_net" />
              </span>
              <span className="tax-value mono">{usd(deferredNet)}</span>
            </div>
          </div>

          {showLongTerm && (
            <div className="tax-row">
              <span>Long-term net</span>
              <span className="mono">{usd(data.long_term_net ?? 0)}</span>
            </div>
          )}

          {income && (
            <div className="tax-row tax-income">
              <span>Income</span>
              <span className="mono">
                div q {usd(income.dividend_qualified ?? 0)} · ord{' '}
                {usd(income.dividend_ordinary ?? 0)} · int{' '}
                {usd(income.interest ?? 0)} · fees {usd(income.fees ?? 0)}
              </span>
            </div>
          )}

          <div className="tax-footer">
            {data.estimated_total_tax != null && (
              <span>Est. tax (configured rates): {usd(data.estimated_total_tax)}</span>
            )}
            {data.calculated_at && (
              <span className="tax-updated">
                updated {relativeTime(data.calculated_at)}
              </span>
            )}
          </div>

          <div className="tax-lots-toggle-row">
            <span className="tax-lots-toggle-label">Show the lots behind these numbers</span>
            <ExpandToggle expanded={expanded} onToggle={toggle} />
          </div>

          {expanded && (
            <div className="tax-lots-section">
              {lotsLoading ? (
                <div className="loading"><div className="spinner" /></div>
              ) : lotsIsError ? (
                <div className="empty-state">
                  <div className="empty-text">Lot ledger unavailable</div>
                </div>
              ) : !lotsData?.available ? (
                lotsData?.degraded ? (
                  <div className="empty-state">
                    <div className="empty-text tax-lots-degraded">
                      lot ledger read failed — this is an error, not an empty ledger
                    </div>
                  </div>
                ) : (
                  <div className="empty-state">
                    <div className="empty-text">{NOT_AVAILABLE_COPY}</div>
                  </div>
                )
              ) : (
                <>
                  <div className="tax-lots-scroll">
                    <table className="tax-lots-table">
                      <thead>
                        <tr>
                          <th scope="col">Symbol</th>
                          <th scope="col">Shares</th>
                          <th scope="col">Acquired → Disposed</th>
                          <th scope="col">Proceeds</th>
                          <th scope="col">Basis</th>
                          <th scope="col">Gain/loss</th>
                          <th scope="col">Reportable</th>
                          <th scope="col">Term</th>
                          <th scope="col">Wash</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lots.map((row, i) => (
                          <LotRow key={`${row.symbol}-${row.acquired}-${row.disposed}-${i}`} row={row} />
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="tax-lots-totals-row">
                          <td className="tax-lots-totals-label" colSpan={3}>{lotsData.truncated ? 'Total (shown)' : 'Total'}</td>
                          <td className="num mono tax-lots-totals-proceeds" data-label="Proceeds">{usd(lotsTotals.proceeds)}</td>
                          <td className="num mono tax-lots-totals-basis" data-label="Basis">{usd(lotsTotals.costBasis)}</td>
                          <td className={`num mono ${lotsTotals.gainLoss < 0 ? 'delta-down' : 'delta-up'} tax-lots-totals-gl`} data-label="Gain/loss">
                            {usd(lotsTotals.gainLoss)}
                          </td>
                          <td className={`num mono ${lotsTotals.reportableGainLoss < 0 ? 'delta-down' : 'delta-up'} tax-lots-totals-reportable`} data-label="Reportable">
                            {usd(lotsTotals.reportableGainLoss)}
                          </td>
                          <td className="tax-lots-totals-empty" colSpan={2} />
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {lotsData.truncated && (
                    <div className="tax-lots-caption">showing latest 2000 lots</div>
                  )}

                  {reconcileResult && (
                    reconcileResult.ok ? (
                      <div className="tax-reconcile tax-reconcile-ok">
                        reconciles with headline ✓
                      </div>
                    ) : staleLots.length > 0 ? (
                      <div className="tax-reconcile tax-reconcile-stale">
                        <strong className="tax-reconcile-flag">STALE HEADLINE</strong>{' '}
                        headline pending tonight's recompute — ledger is live (
                        {staleLots.length} lot{staleLots.length === 1 ? '' : 's'} newer than
                        the headline)
                      </div>
                    ) : (
                      <div className="tax-reconcile tax-reconcile-warn" role="alert">
                        <strong className="tax-reconcile-flag">RECONCILIATION MISMATCH</strong>{' '}
                        ledger and headline disagree
                        (Δ reportable {usd(reconcileResult.deltas.reportable)}) —
                        data-integrity tripwire, do not trust either figure until reconciled
                      </div>
                    )
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
